import { useEffect, useState } from 'react';
import {
  Activity,
  Ban,
  CheckCircle,
  CircleDot,
  Loader2,
  PauseCircle,
  PlayCircle,
  Plus,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  User as UserIcon,
  Wrench,
  XCircle,
} from 'lucide-react';
import type { InterventionHistoriqueAction } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { statutLabel } from './badges';

/**
 * Timeline historique d'une intervention (US-INT-03 + US-INT-04).
 *
 * Lit `/interventions/:id/historique` qui retourne un format spécifique
 * (`type_action`, `ancien_statut`, `nouveau_statut`, `commentaire`,
 * `metadata`).
 */

interface HistoriqueItem {
  id: string;
  intervention_id: string;
  date_action: string;
  type_action: InterventionHistoriqueAction;
  utilisateur_id: string | null;
  ancien_statut: string | null;
  nouveau_statut: string | null;
  commentaire: string | null;
  metadata: Record<string, unknown> | null;
  utilisateur: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
}

const ACTION_LABELS: Record<InterventionHistoriqueAction, string> = {
  creation: 'Création',
  changement_statut: 'Changement de statut',
  affectation: 'Affectation',
  validation: 'Validation',
  rejet_validation: 'Rejet de validation',
  planification: 'Planification',
  demarrage: 'Démarrage',
  pause: 'Mise en pause',
  reprise: 'Reprise',
  pieces_ajoutees: 'Pièces ajoutées',
  cloture: 'Clôture',
  evaluation: 'Évaluation satisfaction',
  annulation: 'Annulation',
};

function actionIcon(action: InterventionHistoriqueAction) {
  switch (action) {
    case 'creation':
      return Sparkles;
    case 'changement_statut':
      return CircleDot;
    case 'affectation':
      return UserIcon;
    case 'validation':
      return ShieldCheck;
    case 'rejet_validation':
      return XCircle;
    case 'planification':
      return Settings;
    case 'demarrage':
      return PlayCircle;
    case 'pause':
      return PauseCircle;
    case 'reprise':
      return PlayCircle;
    case 'pieces_ajoutees':
      return Plus;
    case 'cloture':
      return CheckCircle;
    case 'evaluation':
      return Star;
    case 'annulation':
      return Ban;
    default:
      return Wrench;
  }
}

function actionTone(action: InterventionHistoriqueAction): string {
  switch (action) {
    case 'creation':
      return 'bg-blue-100 text-blue-700';
    case 'cloture':
      return 'bg-green-100 text-green-700';
    case 'annulation':
    case 'rejet_validation':
      return 'bg-red-100 text-red-700';
    case 'validation':
      return 'bg-purple-100 text-purple-700';
    case 'evaluation':
      return 'bg-yellow-100 text-yellow-700';
    case 'pause':
      return 'bg-orange-100 text-orange-700';
    case 'demarrage':
    case 'reprise':
      return 'bg-teal-100 text-teal-700';
    default:
      return 'bg-primary-100 text-primary-700';
  }
}

export function InterventionHistoryTimeline({ interventionId }: { interventionId: string }) {
  const [items, setItems] = useState<HistoriqueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ data: HistoriqueItem[] }>(
          `/interventions/${interventionId}/historique`,
        );
        setItems(res.data.data);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [interventionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  if (errorMessage) {
    return <p className="text-sm text-red-600">{errorMessage}</p>;
  }

  if (items.length === 0) {
    return <p className="text-sm text-gray-500 italic">Aucun événement enregistré.</p>;
  }

  return (
    <ol className="relative border-l border-gray-200 ml-3 space-y-4">
      {items.map((item) => {
        const Icon = actionIcon(item.type_action);
        const tone = actionTone(item.type_action);
        return (
          <li key={item.id} className="ml-5">
            <span
              className={`absolute -left-2.5 flex items-center justify-center w-5 h-5 rounded-full ring-4 ring-white ${tone}`}
            >
              <Icon className="w-3 h-3" />
            </span>
            <div className="rounded-lg border border-gray-200 p-2.5 bg-white text-xs">
              <p className="text-gray-700">
                <span className="font-semibold">
                  {ACTION_LABELS[item.type_action] ?? item.type_action}
                </span>
                {item.type_action === 'changement_statut' &&
                  item.ancien_statut &&
                  item.nouveau_statut && (
                    <>
                      {' '}
                      :{' '}
                      <code className="bg-gray-100 px-1 rounded">
                        {statutLabel(item.ancien_statut as Parameters<typeof statutLabel>[0])}
                      </code>{' '}
                      →{' '}
                      <code className="bg-blue-50 text-blue-700 px-1 rounded">
                        {statutLabel(item.nouveau_statut as Parameters<typeof statutLabel>[0])}
                      </code>
                    </>
                  )}
              </p>
              <p className="text-gray-500 mt-1 flex items-center flex-wrap gap-x-2">
                <span className="inline-flex items-center">
                  <Activity className="w-3 h-3 mr-1" />
                  {new Date(item.date_action).toLocaleString()}
                </span>
                {item.utilisateur && (
                  <span className="inline-flex items-center">
                    <UserIcon className="w-3 h-3 mr-1" />
                    {item.utilisateur.first_name} {item.utilisateur.last_name}
                  </span>
                )}
              </p>
              {item.commentaire && (
                <p className="text-gray-600 mt-1 italic">"{item.commentaire}"</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
